(() => {
  const STORAGE_KEY = 'soundhousCompareV1';
  const FAMILY = 'audio_component';
  const FAMILY_TITLE = 'Choose the right Sonos component';
  const DEFAULT_TITLE = 'Compare products';

  const SCHEMA = [
    {
      label: 'Which is right for you?',
      rows: [
        ['decision.best_for', 'Best for'],
        ['decision.system_scale', 'System scale'],
        ['decision.purchase_route', 'Purchase route'],
        ['decision.key_advantage', 'Key advantage'],
        ['decision.key_compromise', 'Key compromise'],
        ['decision.soundhous_recommendation', 'Soundhous recommendation']
      ]
    },
    {
      label: 'Amplification',
      rows: [
        ['amplification.zones', 'Zones'],
        ['amplification.speaker_outputs', 'Amplified outputs'],
        ['amplification.power', 'Power'],
        ['amplification.output_configuration', 'Output configuration'],
        ['amplification.technology', 'Amplifier technology']
      ]
    },
    {
      label: 'Connectivity',
      rows: [
        ['connectivity.tv', 'TV connection'],
        ['connectivity.analogue_inputs', 'Analogue inputs'],
        ['connectivity.line_output', 'Line output'],
        ['connectivity.trigger', '12V trigger'],
        ['connectivity.network', 'Network'],
        ['connectivity.airplay', 'Apple AirPlay 2']
      ]
    },
    {
      label: 'Tuning',
      rows: [
        ['tuning.control', 'Tuning control'],
        ['tuning.speaker_optimization', 'Speaker optimisation']
      ]
    },
    {
      label: 'Installation',
      rows: [
        ['installation.format', 'Installation format'],
        ['installation.dimensions', 'Dimensions'],
        ['installation.weight', 'Weight']
      ]
    }
  ];

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const getPath = (object, path) => path.split('.').reduce((value, key) => (
    value && Object.prototype.hasOwnProperty.call(value, key) ? value[key] : null
  ), object);

  const getValue = (product, path) => {
    const value = getPath(product?.profile, path);
    if (value != null && String(value).trim() !== '') return value;

    if (path === 'connectivity.line_output') {
      if (product?.handle === 'sonos-amp' || product?.handle === 'sonos-amp-multi') {
        return 'No line-level output';
      }
    }

    if (path === 'connectivity.trigger') {
      if (product?.handle === 'sonos-amp' || product?.handle === 'sonos-amp-multi') {
        return 'No';
      }
    }

    return null;
  };

  const normalize = (value) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

  const loadSelection = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.handle && item.profile) : [];
    } catch (error) {
      return [];
    }
  };

  const familySelection = () => loadSelection().filter((item) => item?.profile?.family === FAMILY);

  const cleanupRows = (tbody) => {
    tbody?.querySelectorAll('[data-shcmp-audio-component-row="true"]').forEach((row) => row.remove());
  };

  const renderRows = (root) => {
    if (!root) return;

    const selection = familySelection();
    const title = root.querySelector('[id^="SoundhousCompareTitle-"]');
    const modal = root.querySelector('[data-shcmp-modal]');
    const tableWrap = root.querySelector('[data-shcmp-table]');
    const tbody = tableWrap?.querySelector('.shcmp-table tbody');

    if (title) {
      title.textContent = selection.length ? FAMILY_TITLE : DEFAULT_TITLE;
    }

    if (!tbody) return;
    cleanupRows(tbody);
    if (!modal || modal.hidden || selection.length < 2) return;

    const existingCoreRows = [...tbody.querySelectorAll('.shcmp-data-row')]
      .filter((row) => row.dataset.shcmpAudioComponentRow !== 'true');
    if (existingCoreRows.length > 0) return;

    const onlyDifferences = Boolean(root.querySelector('[data-shcmp-differences]')?.checked);

    const markup = SCHEMA.map((group, groupIndex) => {
      const rowMarkup = group.rows.map(([path, label]) => {
        const values = selection.map((product) => getValue(product, path));
        const normalized = values.map(normalize).filter(Boolean);
        const unique = new Set(normalized);
        const hasAnyValue = normalized.length > 0;
        const isDifferent = unique.size > 1 || normalized.length !== values.length;

        if (!hasAnyValue || (onlyDifferences && !isDifferent)) return '';

        const rowClasses = [
          'shcmp-data-row',
          path.startsWith('decision.') ? 'is-guidance' : '',
          path === 'decision.soundhous_recommendation' ? 'is-recommendation' : ''
        ].filter(Boolean).join(' ');

        const valueCells = values.map((value) => (
          `<td class="shcmp-value">${escapeHtml(value ?? '—')}</td>`
        )).join('');

        return `
          <tr class="${rowClasses}" data-different="${isDifferent ? 'true' : 'false'}" data-shcmp-audio-component-row="true">
            <th scope="row" class="shcmp-label">${escapeHtml(label)}</th>
            ${valueCells}
          </tr>
        `;
      }).join('');

      if (!rowMarkup) return '';

      return `
        <tr class="shcmp-group-row${groupIndex === 0 ? ' shcmp-group-row--guidance' : ''}" data-shcmp-audio-component-row="true">
          <th colspan="${selection.length + 1}">${escapeHtml(group.label)}</th>
        </tr>
        ${rowMarkup}
      `;
    }).join('');

    if (markup) tbody.insertAdjacentHTML('beforeend', markup);
  };

  const bindRoot = (root) => {
    if (!root || root.dataset.shcmpAudioComponentV1Ready === 'true') return;
    root.dataset.shcmpAudioComponentV1Ready = 'true';

    let queued = false;
    const scheduleRender = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        renderRows(root);
      });
    };

    const observer = new MutationObserver(scheduleRender);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'aria-pressed']
    });

    root.querySelector('[data-shcmp-differences]')?.addEventListener('change', scheduleRender);
    root.querySelector('[data-shcmp-clear]')?.addEventListener('click', scheduleRender);
    root.querySelector('[data-shcmp-open]')?.addEventListener('click', scheduleRender);
    root.querySelector('[data-shcmp-close]')?.addEventListener('click', scheduleRender);
    root.addEventListener('click', (event) => {
      if (event.target.closest?.('[data-shcmp-remove]')) scheduleRender();
    });

    scheduleRender();
  };

  const init = () => {
    document.querySelectorAll('[data-soundhous-compare-root]').forEach(bindRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
